import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cube, EnvelopeSimple, Users, Eye } from '@phosphor-icons/react';
import axios from 'axios';
import { API, useAuth } from '../../App';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    concepts: 0,
    contacts: 0,
    newContacts: 0
  });
  const [recentContacts, setRecentContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [conceptsRes, contactsRes] = await Promise.all([
          axios.get(`${API}/concepts`),
          axios.get(`${API}/admin/contacts`, { withCredentials: true })
        ]);

        const contacts = contactsRes.data;
        const newContacts = contacts.filter(c => c.status === 'new');

        setStats({
          concepts: conceptsRes.data.length,
          contacts: contacts.length,
          newContacts: newContacts.length
        });

        setRecentContacts(contacts.slice(0, 5));
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const statCards = [
    {
      label: 'Concepts',
      value: stats.concepts,
      icon: Cube,
      link: '/admin/concepts',
      color: 'text-blue-400'
    },
    {
      label: 'Demandes',
      value: stats.contacts,
      icon: EnvelopeSimple,
      link: '/admin/contacts',
      color: 'text-green-400'
    },
    {
      label: 'Nouvelles',
      value: stats.newContacts,
      icon: Users,
      link: '/admin/contacts',
      color: 'text-[#C5A059]'
    }
  ];

  return (
    <div data-testid="admin-dashboard">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-display text-3xl text-white mb-2">
          Bonjour, {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-stone-400">
          Bienvenue dans votre espace d'administration Studio Bar
        </p>
      </div>

      {/* Stats */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {statCards.map((stat) => {
              const Icon = stat.icon;
              return (
                <Link
                  key={stat.label}
                  to={stat.link}
                  className="admin-card p-6 hover:border-white/10 transition-colors"
                >
                  <div className="flex items-center justify-between mb-4">
                    <Icon size={24} weight="light" className={stat.color} />
                    <span className="text-3xl font-display text-white">{stat.value}</span>
                  </div>
                  <p className="text-stone-400 text-sm">{stat.label}</p>
                </Link>
              );
            })}
          </div>

          {/* Recent Contacts */}
          <div className="admin-card">
            <div className="flex items-center justify-between p-6 border-b border-white/5">
              <h2 className="font-display text-xl text-white">Demandes Récentes</h2>
              <Link 
                to="/admin/contacts"
                className="text-sm text-[#C5A059] hover:text-white transition-colors"
              >
                Voir tout
              </Link>
            </div>

            {recentContacts.length === 0 ? (
              <div className="p-6 text-center text-stone-400">
                Aucune demande pour le moment
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {recentContacts.map((contact) => (
                  <div key={contact.id} className="p-6 flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium">{contact.name}</p>
                      <p className="text-sm text-stone-400">{contact.email}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`text-xs px-2 py-1 rounded ${
                        contact.status === 'new' 
                          ? 'bg-[#C5A059]/20 text-[#C5A059]' 
                          : 'bg-stone-800 text-stone-400'
                      }`}>
                        {contact.status === 'new' ? 'Nouveau' : contact.status}
                      </span>
                      <span className="text-xs text-stone-500">
                        {new Date(contact.created_at).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link
              to="/admin/concepts"
              data-testid="quick-action-concepts"
              className="admin-card p-6 hover:border-[#C5A059]/30 transition-colors group"
            >
              <h3 className="font-display text-lg text-white mb-2 group-hover:text-[#C5A059] transition-colors">
                Gérer les Concepts
              </h3>
              <p className="text-sm text-stone-400">
                Ajouter, modifier ou supprimer des concepts de bars
              </p>
            </Link>

            <Link
              to="/"
              target="_blank"
              data-testid="quick-action-preview"
              className="admin-card p-6 hover:border-[#C5A059]/30 transition-colors group"
            >
              <div className="flex items-center gap-2 mb-2">
                <h3 className="font-display text-lg text-white group-hover:text-[#C5A059] transition-colors">
                  Voir le Site
                </h3>
                <Eye size={18} weight="light" className="text-stone-400" />
              </div>
              <p className="text-sm text-stone-400">
                Prévisualiser le site public
              </p>
            </Link>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
